import React, { useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  Chip,
  IconButton,
  Collapse,
  Stack,
  InputAdornment,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import type { StudentRosterEntry, StudentCheckpointDetail, AttemptLogEntry } from '../services/api';

function fmtMs(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

function StatusIcon({ status }: { status: StudentCheckpointDetail['status'] }) {
  if (status === 'solved') return <CheckCircleIcon fontSize="small" color="success" />;
  if (status === 'attempted') return <HelpOutlineIcon fontSize="small" color="warning" />;
  return <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
}

function parseLabel_(checkpointId: string): string {
  const lastDot = checkpointId.lastIndexOf('.');
  const lastSlash = checkpointId.lastIndexOf('/');
  const sep = Math.max(lastDot, lastSlash);
  if (sep <= 0 || sep >= checkpointId.length - 1) return checkpointId;
  return checkpointId.substring(sep + 1);
}

function formatTime(iso: string): string {
  const t = new Date(iso);
  return `${t.getHours().toString().padStart(2, '0')}:${t.getMinutes().toString().padStart(2, '0')}:${t.getSeconds().toString().padStart(2, '0')}`;
}

function ChronologyView({ chronology }: { chronology: AttemptLogEntry[] }) {
  // chronology is reverse-chronological; show oldest first for readability.
  const ordered = [...chronology].reverse();
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}
      >
        Chronology ({chronology.length}{chronology.length === 50 ? '+' : ''})
      </Typography>
      <Box
        sx={{
          borderLeft: '2px solid',
          borderColor: 'divider',
          pl: 1.5,
          maxHeight: 220,
          overflowY: 'auto',
        }}
      >
        {ordered.map((e, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, fontSize: '0.78rem' }}>
            {e.is_correct ? (
              <CheckCircleIcon fontSize="inherit" color="success" />
            ) : (
              <HelpOutlineIcon fontSize="inherit" color="warning" />
            )}
            <Box component="code" sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: 'text.secondary', minWidth: 140 }}>
              {parseLabel_(e.checkpoint_id)}
            </Box>
            <Box
              component="code"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.72rem',
                color: e.is_correct ? 'success.main' : 'text.primary',
                fontWeight: e.is_correct ? 600 : 400,
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {e.submitted_value ?? '(empty)'}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', flexGrow: 1 }}>
              attempt #{e.attempt_num}
              {e.elapsed_ms != null && ` · ${e.elapsed_ms < 1000 ? `${e.elapsed_ms}ms` : `${(e.elapsed_ms / 1000).toFixed(1)}s`}`}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.68rem' }}>
              {formatTime(e.created_at)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function parseLabel(checkpointId: string): string {
  const lastDot = checkpointId.lastIndexOf('.');
  const lastSlash = checkpointId.lastIndexOf('/');
  const sep = Math.max(lastDot, lastSlash);
  if (sep <= 0 || sep >= checkpointId.length - 1) return checkpointId;
  return checkpointId.substring(sep + 1);
}

function StudentRow({ entry }: { entry: StudentRosterEntry }) {
  const [open, setOpen] = useState(false);
  const total = entry.per_checkpoint.length;
  const fullySolved = entry.checkpoints_solved === total && total > 0;
  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.75, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label="expand" sx={{ p: 0.5 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography sx={{ flexGrow: 1, fontWeight: 500, fontSize: '0.9rem' }}>
          {entry.display_name}
        </Typography>
        {entry.current_checkpoint_id && (
          <Tooltip title="Most recent attempt — where they are right now">
            <Chip
              size="small"
              label={parseLabel(entry.current_checkpoint_id)}
              variant="outlined"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.72rem',
                color: 'primary.main',
                borderColor: 'primary.main',
                backgroundColor: '#e6f5f3',
                height: 22,
              }}
            />
          </Tooltip>
        )}
        <Typography
          variant="caption"
          sx={{
            color: fullySolved ? 'success.main' : 'text.secondary',
            fontWeight: fullySolved ? 600 : 500,
            fontVariantNumeric: 'tabular-nums',
            minWidth: 70,
            textAlign: 'right',
          }}
        >
          {entry.checkpoints_solved}/{total} solved
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', minWidth: 70, textAlign: 'right' }}>
          {entry.total_attempts} tries
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', minWidth: 80, textAlign: 'right' }}>
          {entry.fastest_elapsed_ms != null ? `⚡ ${fmtMs(entry.fastest_elapsed_ms)}` : ''}
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 5, pt: 1, pb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}
          >
            Per checkpoint
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 1.5 }}>
            {entry.per_checkpoint.map((cp) => (
              <Box key={cp.checkpoint_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StatusIcon status={cp.status} />
                <Box component="code" sx={{ minWidth: 180, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem' }}>
                  {cp.checkpoint_id}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {cp.status === 'solved'
                    ? `solved on attempt ${cp.first_correct_attempt} · ${fmtMs(cp.elapsed_ms_first_correct)}`
                    : cp.status === 'attempted'
                      ? `${cp.attempts} wrong attempt${cp.attempts === 1 ? '' : 's'}, no correct yet`
                      : 'not yet attempted'}
                </Typography>
              </Box>
            ))}
          </Stack>
          {entry.chronology && entry.chronology.length > 0 && (
            <ChronologyView chronology={entry.chronology} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

interface TeamGroup {
  team_id: string;
  team_name: string;
  members: StudentRosterEntry[];
  // Union of solved checkpoints across members / summed tries / fastest member.
  checkpoints_solved: number;
  total_checkpoints: number;
  total_attempts: number;
  fastest_elapsed_ms: number | null;
}

// Roll a team's members up into one line: a checkpoint counts as solved if ANY
// member solved it (the team got the answer), tries are summed, and the team's
// headline speed is its fastest member.
function aggregateTeam(team_id: string, team_name: string, members: StudentRosterEntry[]): TeamGroup {
  const checkpointIds = members[0]?.per_checkpoint.map((c) => c.checkpoint_id) ?? [];
  let solved = 0;
  for (const cpId of checkpointIds) {
    const anySolved = members.some((m) =>
      m.per_checkpoint.some((c) => c.checkpoint_id === cpId && c.status === 'solved'),
    );
    if (anySolved) solved += 1;
  }
  const total_attempts = members.reduce((sum, m) => sum + m.total_attempts, 0);
  const fastest = members
    .map((m) => m.fastest_elapsed_ms)
    .filter((x): x is number => x != null);
  return {
    team_id,
    team_name,
    members,
    checkpoints_solved: solved,
    total_checkpoints: checkpointIds.length,
    total_attempts,
    fastest_elapsed_ms: fastest.length ? Math.min(...fastest) : null,
  };
}

function TeamRow({ team }: { team: TeamGroup }) {
  const [open, setOpen] = useState(false);
  const fullySolved = team.checkpoints_solved === team.total_checkpoints && team.total_checkpoints > 0;
  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.75, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label="expand" sx={{ p: 0.5 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <GroupsIcon fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{team.team_name}</Typography>
        <Chip
          size="small"
          label={`${team.members.length} member${team.members.length === 1 ? '' : 's'}`}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.68rem' }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Typography
          variant="caption"
          sx={{
            color: fullySolved ? 'success.main' : 'text.secondary',
            fontWeight: fullySolved ? 600 : 500,
            fontVariantNumeric: 'tabular-nums',
            minWidth: 70,
            textAlign: 'right',
          }}
        >
          {team.checkpoints_solved}/{team.total_checkpoints} solved
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', minWidth: 70, textAlign: 'right' }}>
          {team.total_attempts} tries
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', minWidth: 80, textAlign: 'right' }}>
          {team.fastest_elapsed_ms != null ? `⚡ ${fmtMs(team.fastest_elapsed_ms)}` : ''}
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 3 }}>
          {team.members.map((m) => (
            <StudentRow key={m.session_id} entry={m} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

const StudentRoster: React.FC<{ roster: StudentRosterEntry[] }> = ({ roster }) => {
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'student' | 'team'>('student');

  const hasTeams = useMemo(() => roster.some((r) => r.team_id), [roster]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return roster;
    return roster.filter((r) => r.display_name.toLowerCase().includes(needle));
  }, [roster, filter]);

  // Group the (filtered) roster into teams; ungrouped students fall through as
  // solo rows so nobody disappears in team view.
  const { teams, solo } = useMemo(() => {
    const byTeam = new Map<string, { name: string; members: StudentRosterEntry[] }>();
    const ungrouped: StudentRosterEntry[] = [];
    for (const r of filtered) {
      if (r.team_id) {
        const g = byTeam.get(r.team_id) ?? { name: r.team_name ?? 'Team', members: [] };
        g.members.push(r);
        byTeam.set(r.team_id, g);
      } else {
        ungrouped.push(r);
      }
    }
    const teamGroups = Array.from(byTeam.entries())
      .map(([id, g]) => aggregateTeam(id, g.name, g.members))
      .sort((a, b) => b.checkpoints_solved - a.checkpoints_solved);
    return { teams: teamGroups, solo: ungrouped };
  }, [filtered]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flexGrow: 1 }}
            onClick={() => setOpen((o) => !o)}
          >
            <IconButton size="small" aria-label="toggle roster">
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography variant="h6">Roster ({roster.length})</Typography>
            {!open && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                click to expand
              </Typography>
            )}
          </Box>
          {open && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {hasTeams && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={mode}
                  onChange={(_, v) => v && setMode(v)}
                >
                  <ToggleButton value="student" sx={{ textTransform: 'none', py: 0.25 }}>
                    By student
                  </ToggleButton>
                  <ToggleButton value="team" sx={{ textTransform: 'none', py: 0.25 }}>
                    By team
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              <TextField
                size="small"
                placeholder="filter by name…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ width: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </Box>
        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 1 }}>
            {filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {roster.length === 0 ? 'No students yet.' : 'No matches.'}
              </Typography>
            ) : mode === 'team' && hasTeams ? (
              <>
                {teams.map((t) => (
                  <TeamRow key={t.team_id} team={t} />
                ))}
                {solo.length > 0 && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 1.5, mb: 0.5, px: 1, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}
                    >
                      No team
                    </Typography>
                    {solo.map((r) => (
                      <StudentRow key={r.session_id} entry={r} />
                    ))}
                  </>
                )}
              </>
            ) : (
              filtered.map((r) => <StudentRow key={r.session_id} entry={r} />)
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default StudentRoster;
